import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { debounce, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { withConfig } from 'util/hoc';
import { resolve } from 'library/helpers/path-resolver';
import { Random } from 'library/helpers/random';
import Notification, { Info } from 'library/components/notification';
import PlusSvg from 'library/icons/plus.svg';
import { Header5 } from 'library/components/text-elements/headings';
import InputField from 'apps/journey/components/input-field/input-field';
import SearchQuery from 'apps/roi/graph-calls/queries/roi-search-programs';
import * as StyledRoiChart from 'apps/roi/components/charts/styled-roi-chart';
import {
  CheckBoxWrapper,
  StyledColumn,
  StyledRow,
  StyledSurveyOptionsWrapper,
} from 'apps/roi/pages/roi-survey/styled-roi-survey';
import * as StyledRoiSearch from 'apps/roi/pages/styled-roi-search';
import { CheckboxStatus } from 'apps/roi/pages/roi-survey/components/checkbox-status';

const searchPrograms = async (
  inputValue,
  progMap,
  limit,
  resultsCb,
  loadingCb,
  totalCb,
  degreeType
) => {
  const increasedLimit = limit + Object.keys(progMap).length;
  try {
    const { data } = await SearchQuery(inputValue, increasedLimit, degreeType);
    const allResults = resolve(data, 'search_programs/results', []);
    const filteredResults = allResults.filter(
      (result) =>
        !Object.keys(progMap)
          .map((c) => Number(c))
          .includes(result['id'])
    );
    resultsCb(inputValue.length ? filteredResults : []);
    totalCb(resolve(data, 'search_programs/pagination/total', 0));
  } catch (e) {
    console.error(e);
  }
  loadingCb(false);
};

const SearchPrograms = ({
  answers,
  mainQuestion,
  programMap,
  setProgramMap,
  setAnswer,
  loading,
  setLoading,
  configuration,
}) => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);
  const [searchResultsFocus, setSearchResultsFocus] = useState(false);

  const searchResultsRef = useRef(null);
  const showMoreRef = useRef(null);
  const searchResultBoxRef = useRef(null);
  const optionsWrapperRef = useRef(null);

  useEffect(() => {
    if (isEmpty(answers)) {
      setSearchText('');
      setResults([]);
      setTotal(0);
      setLimit(25);
    }
  }, [answers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doSearch = useCallback(debounce(searchPrograms, 300), []);

  const degreeType = answers['degree_type'];

  const handleSearchText = async (e) => {
    setLoading(true);
    const inputValue = e.target.value;
    setSearchText(inputValue);
    doSearch(
      inputValue,
      programMap,
      limit,
      setResults,
      setLoading,
      setTotal,
      degreeType
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClick = (e) => {
    if (
      searchResultsFocus &&
      !searchResultBoxRef?.current?.contains(e.target) &&
      !optionsWrapperRef?.current?.contains(e.target) &&
      e.target !== searchResultBoxRef?.current &&
      e.target !== optionsWrapperRef?.current
    ) {
      setSearchResultsFocus(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-undef
    mount.addEventListener('click', handleClick);
    return () => {
      // eslint-disable-next-line no-undef
      mount.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  const showMore = () => {
    setLoading(true);
    const newLimit = limit + 10;
    setLimit(newLimit);
    doSearch(
      searchText,
      programMap,
      newLimit,
      setResults,
      setLoading,
      setTotal,
      degreeType
    );
  };

  const selectProgram = (programId) => {
    setLoading(true);
    const selectedProgram = results.find((program) => program.id === programId);
    const { degree, provider, cip } = selectedProgram;
    const eventLabel = `${provider.name} - ${cip.title.substring(
      0,
      cip.title.length - 1
    )} | ${degree.name}`;
    try {
      ga('send', 'event', 'SelectRoiProgram', 'click', eventLabel);
    } catch (e) {
      console.error(`Can't reach google analytics at the moment.`, e);
    }

    if (Object.keys(programMap).length < 7) {
      const colorPalette = resolve(
        configuration,
        'environment/journey/tools/graph_color_palette',
        StyledRoiChart.DefaultColorPalette
      );
      const unUsedColors = colorPalette.filter(
        (c) => !Object.values(programMap).includes(c)
      );
      programMap[programId] = unUsedColors[Random(0, unUsedColors.length - 1)];

      setProgramMap(programMap);
      const programIds = answers[mainQuestion.key_to_update] || [];
      if (!programIds.some((id) => id === programId)) {
        programIds.push(programId);
      }
      setAnswer(mainQuestion.key_to_update, programIds);
      setResults([...results.deleteValue(selectedProgram)]);
    } else {
      Notification.new(
        { timeout: 5000 },
        <Info> You may only add seven programs to the chart at one time </Info>
      );
    }
    setLoading(false);
  };

  const mapResults = ({ id, provider, degree, cip }) => {
    const Component = programMap.length
      ? StyledRoiSearch.StyledSearchResultCard
      : StyledRoiSearch.StyledSearchResultRow;

    return (
      <Component
        ref={searchResultsRef}
        onClick={() => selectProgram(id)}
        tabIndex={0}
        key={id}
      >
        <p>
          {' '}
          <b> {provider.name} </b> -{' '}
          {cip.title.substring(0, cip.title.length - 1)} | {degree.name}{' '}
        </p>
      </Component>
    );
  };

  const renderedSearchResults = () => {
    const hasSearchResults = searchText.length > 0 && total > 0;
    const noSearchResults = searchText.length > 0 && !loading && total === 0;

    return (
      <StyledRoiSearch.StyledSearchResults ref={searchResultBoxRef}>
        {hasSearchResults ? (
          <Fragment>
            {results.map(mapResults)}
            {limit < total && (
              <StyledRoiSearch.StyledShowMoreButton
                ref={showMoreRef}
                onClick={showMore}
              >
                Show More <PlusSvg />
              </StyledRoiSearch.StyledShowMoreButton>
            )}
          </Fragment>
        ) : (
          noSearchResults && (
            <Fragment>
              <StyledRoiSearch.StyledSearchResultCard>
                No Results
              </StyledRoiSearch.StyledSearchResultCard>
            </Fragment>
          )
        )}
      </StyledRoiSearch.StyledSearchResults>
    );
  };

  return (
    <StyledRow>
      <CheckBoxWrapper>
        <CheckboxStatus
          active={answers[mainQuestion.key_to_update]?.length > 0}
        />
      </CheckBoxWrapper>
      <StyledColumn>
        <StyledRow>
          <Header5>{mainQuestion?.text}</Header5>
        </StyledRow>
        <StyledSurveyOptionsWrapper
          ref={optionsWrapperRef}
          onFocus={() => {
            setSearchResultsFocus(true);
          }}
          onBlur={({ relatedTarget }) => {
            if (
              relatedTarget &&
              (relatedTarget?.className ===
                searchResultsRef?.current?.className ||
                showMoreRef?.current?.className === relatedTarget?.className)
            ) {
              return;
            }
            return setSearchResultsFocus(false);
          }}
        >
          <InputField
            value={searchText}
            label="Search for a school or programs"
            onChange={handleSearchText}
            ignoreState
          />
          {searchResultsFocus && renderedSearchResults()}
        </StyledSurveyOptionsWrapper>
      </StyledColumn>
    </StyledRow>
  );
};

SearchPrograms.propTypes = {
  answers: PropTypes.object,
  mainQuestion: PropTypes.object,
  programMap: PropTypes.object,
  setProgramMap: PropTypes.func,
  setAnswer: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  configuration: PropTypes.object,
};

export default withConfig(SearchPrograms);
